import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { FormBuilder, FormGroup, FormControl, ValidationErrors } from '@angular/forms';

import { PasswordRecoveryService } from '../../services/password-recovery.service';
import { Router, ActivatedRoute } from '@angular/router';
import {
  minimumLengthRequired,
  lowerCaseRequired,
  upperCaseRequired,
  digitRequired,
  equalPasswordValidator,
  forbiddenNameValidator
} from './change-password-form.directive';
import { UserData, ValidatePasswordDataResponse, SessionError, ApplicationError, ServerError, ErrorType } from 'src/app/models';
import { ErrorsURLs } from 'src/app/errors/errors.url';
import { PagesURLs } from '../pages.url';
import { AppSession } from 'src/app/models/app-session';
import {finalize} from 'rxjs/operators';

@Component({
  selector: 'app-change-password-form',
  templateUrl: './change-password-form.component.html',
  styleUrls: ['./change-password-form.component.css'],
})
export class ChangePasswordFormComponent implements OnInit {

  hide = true;
  hideConfirm = true;
  loading = false;

  submitted = false;

  inputType = 'password';
  minPasswordLength = AppSession.getPasswordPolicy().minLength;
  minPasswordUppercase = AppSession.getPasswordPolicy().minUppercase;
  minPasswordLowercase = AppSession.getPasswordPolicy().minLowercase;
  minPasswordDigits = AppSession.getPasswordPolicy().minDigits;
  minPasswordHistory = AppSession.getPasswordPolicy().minHistory;

  minPasswordLengthText = this.minPasswordLength;
  minPasswordUppercaseText = this.minPasswordUppercase;
  minPasswordLowercaseText = this.minPasswordLowercase;
  minPasswordDigitsText = this.minPasswordDigits;
  minPasswordHistoryText = this.minPasswordHistory;

  passwordForm = new FormGroup({
    newPassword: new FormControl(),
    confirmPassword: new FormControl()
  });

  @ViewChild('showButton') public showButton: ElementRef<HTMLElement>;

  constructor(
    public translate: TranslateService,
    private formBuilder: FormBuilder,
    private passwordRecoveryService: PasswordRecoveryService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userData: UserData
  ) { }

  get getForm() { return this.passwordForm.controls; }

  ngOnInit(): void {

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      if (this.translate.currentLang === 'en') {
        this.minPasswordLengthText = this.translate.instant('numbers.' + this.minPasswordLength);
        this.minPasswordUppercaseText = this.translate.instant('numbers.' + this.minPasswordUppercase);
        this.minPasswordLowercaseText = this.translate.instant('numbers.' + this.minPasswordLowercase);
        this.minPasswordDigitsText = this.translate.instant('numbers.' + this.minPasswordDigits);
        this.minPasswordHistoryText = this.translate.instant('numbers.' + this.minPasswordHistory);
      }
    });

    this.passwordForm = this.formBuilder.group({
      newPassword: new FormControl('',
        { validators: [
          forbiddenNameValidator(this.userData.username),
          forbiddenNameValidator(this.userData.name),
          forbiddenNameValidator(this.userData.firstName),
          forbiddenNameValidator(this.userData.lastName),
          minimumLengthRequired(this.minPasswordLength),
          lowerCaseRequired(),
          upperCaseRequired(),
          digitRequired()]
        }),
      confirmPassword: new FormControl('', [])
    }, { validators: [equalPasswordValidator()] });

  }

  onSubmit() {
    this.submitted = true;

    const newPassword = this.getForm.newPassword.value;
    const confirmPassword = this.getForm.confirmPassword.value;
    if (newPassword === ''
      || confirmPassword === ''
      || this.passwordForm.invalid)
    {
      return;
    }

    if (newPassword === confirmPassword ) {
      this.changePassword(newPassword);
    }
    return;
  }

  private changePassword(newPassword: string) {
    try {
      this.loading = true;
      this.passwordRecoveryService.changePassword(newPassword)
        .pipe(
          finalize(() => this.loading = false)
        )
        .subscribe(
          (response: ValidatePasswordDataResponse) => {
            console.log('Password change request send: ' + response);
            this.router.navigate([PagesURLs.CHANGE_PASSWORD_SUCCESS], { relativeTo: this.activatedRoute.root, skipLocationChange: true });
          },
          (error: Error) => {
            this.processError(error);
          }
        );
    }
    catch (error) {
      console.error('Change password request failed...');
      // console.error(error.name);
      this.router.navigate([ErrorsURLs.REQUEST_FAILED], { relativeTo: this.activatedRoute.root, skipLocationChange: true });
    }
  }

  newPasswordValidator() {
    const newPassword = this.getForm.newPassword.value;
    if (newPassword !== '') {
      try {
        this.loading = true;
        this.passwordRecoveryService.validatePassword(newPassword)
          .pipe(
            finalize(() => this.loading = false)
          )
          .subscribe(
            (response: ValidatePasswordDataResponse) => {
              console.log('newPassword OK');
              if (response !== null) {
                console.log(response);
                const tokenData = response.data[0];
                this.userData.tokensession = tokenData.tokensession;
              }
            },
            (error: Error) => {
              console.warn(error);
              // Do nothing in background checks for UX
              this.processError(error);
            }
          );
      }
      catch (error) {
        console.error('password validation failed...');
        console.error(error.name);
        this.router.navigate([ErrorsURLs.REQUEST_FAILED], { relativeTo: this.activatedRoute.root, skipLocationChange: true });
      }
    }
  }

  private processError(error: Error) {
    if (error instanceof SessionError){
      // console.warn(error.name);
      this.router.navigate([ErrorsURLs.INVALID_SESSION], { relativeTo: this.activatedRoute.root, skipLocationChange: true });
    }
    else if (error instanceof ApplicationError){
      let eList = this.getForm.newPassword.errors;
      // console.log(eList);
      if (error.errors === undefined) {
        console.warn('error.errors === undefined');
      } else {
        const controlErrors: ValidationErrors = error.errors;
        Object.keys(error.errors).forEach(i => {
          const errorType: ErrorType = error.errors[i];
          // console.log(errorType.code + ':' + errorType.message);
          console.log('err value: ', errorType);
          const errorCode = errorType.code;
          if (eList === null) {
            eList = [];
          }
          eList[errorCode] = errorType.message;
        });

        // Invalid token
        if (+(error.errors[0].code) === +(ErrorType._4201.code)) {
          this.router.navigate([ErrorsURLs.REQUEST_FAILED], { relativeTo: this.activatedRoute.root, skipLocationChange: true });
        } else {
          if (eList != null) {
            this.getForm.newPassword.setErrors(eList);
          } else {
            this.getForm.newPassword.setErrors(controlErrors);
          }
        }


      }
      // console.log(eList);
      // this.getForm.newPassword.setErrors(eList);
    }
    else if (error instanceof ServerError){
      // console.error(error.name);
      if (error.errors === undefined) {
        this.router.navigate([ErrorsURLs.REQUEST_FAILED], { relativeTo: this.activatedRoute.root, skipLocationChange: true });
      }
      else if (+(error.errors[0].code) === +(ErrorType._5401.code)) {
        this.router.navigate([ErrorsURLs.REQUEST_FAILED], { relativeTo: this.activatedRoute.root, skipLocationChange: true });
      }
      else if (+(error.errors[0].code) === +(ErrorType._5402.code)) {
        this.router.navigate([ErrorsURLs.CHANGE_PASSWORD_FAILED], { relativeTo: this.activatedRoute.root, skipLocationChange: true });
      }
      else {
        this.router.navigate([ErrorsURLs.REQUEST_FAILED], { relativeTo: this.activatedRoute.root, skipLocationChange: true });
      }
    }
    else {
      // console.error(error.name);
      this.router.navigate([ErrorsURLs.REQUEST_FAILED], { relativeTo: this.activatedRoute.root, skipLocationChange: true });
    }
  }

  getFormValidationErrors(controlErrors: ValidationErrors) {
    console.log(controlErrors);
    const result = [];
    if (controlErrors) {
      Object.keys(controlErrors).forEach(keyError => {
        result.push(keyError);
      });
    }
    return this.getTextErrors(result);
  }

  private getTextErrors(fieldErrors: Array<any>) {
    // console.log(fieldErrors);
    let minErrors = 0;
    Object.keys(fieldErrors).forEach((value, index) => {
      const errorCode = +(fieldErrors[index]);
      if (
        errorCode === ErrorType._4401.code ||
        errorCode === ErrorType._4402.code ||
        errorCode === ErrorType._4403.code ||
        errorCode === ErrorType._4404.code
      ) {
        minErrors++;
      }
      });

    const initialText = this.translate.instant('pages.change-password.errors.text');
    const otherInitialText = this.translate.instant('pages.change-password.errors.other-text');
    let text = '';
    let checkForInitialText = false;
    let checkForOtherInitialText = false;
    Object.keys(fieldErrors).forEach((value, index) => {
      const errorCode = +(fieldErrors[index]);
      if (index > 0 && index < (minErrors - 1)) {
        if (
          errorCode === ErrorType._4401.code ||
          errorCode === ErrorType._4402.code ||
          errorCode === ErrorType._4403.code ||
          errorCode === ErrorType._4404.code
        ) {
          text += this.translate.instant('pages.change-password.errors.comma');
        }
      }
      if (index === (minErrors - 1) && index !== 0) {
        text += this.translate.instant('pages.change-password.errors.and');
      }

      let textValue = '';
      switch (errorCode) {
        case ErrorType._4401.code: {
          textValue = this.translate.instant('pages.change-password.errors.' + errorCode,
            { minPasswordLength: this.minPasswordLengthText });
          checkForInitialText = true;
          break;
        }
        case ErrorType._4402.code: {
          const plural = (+(this.minPasswordUppercaseText) > 1) ? 's' : '';
          textValue = this.translate.instant('pages.change-password.errors.' + errorCode + plural,
            { minPasswordUppercase: this.minPasswordUppercaseText });
          checkForInitialText = true;
          break;
        }
        case ErrorType._4403.code: {
          const plural = (+(this.minPasswordLowercaseText) > 1) ? 's' : '';
          textValue = this.translate.instant('pages.change-password.errors.' + errorCode + plural,
            { minPasswordLowercase: this.minPasswordLowercaseText });
          checkForInitialText = true;
          break;
        }
        case ErrorType._4404.code: {
          const plural = (+(this.minPasswordDigitsText) > 1) ? 's' : '';
          textValue = this.translate.instant('pages.change-password.errors.' + errorCode + plural,
            { minPasswordDigits: this.minPasswordDigitsText });
          checkForInitialText = true;
          break;
        }
        case ErrorType._4405.code: {
          if (index !== 0) {
            text += this.translate.instant('pages.change-password.errors.point');
            if (this.translate.currentLang === 'en') {
              textValue += this.translate.instant('pages.change-password.errors.it');
            }
          }
          textValue += this.translate.instant('pages.change-password.errors.' + errorCode);
          if (index === 0) {
            checkForOtherInitialText = true;
          }
          else { textValue = this.capitalize(textValue); }
          break;
        }
        case ErrorType._4406.code: {
          const plural = (+(this.minPasswordHistoryText) > 1) ? 's' : '';
          if (index !== 0) {
            text += this.translate.instant('pages.change-password.errors.point');
            if (this.translate.currentLang === 'en') {
              textValue += this.translate.instant('pages.change-password.errors.it');
            }
          }
          textValue += this.translate.instant('pages.change-password.errors.' + errorCode + plural,
            { minPasswordHistory: this.minPasswordHistoryText });
          if (index === 0) {
            checkForOtherInitialText = true;
          }
          else { textValue = this.capitalize(textValue); }
          break;
        }
        case ErrorType._4407.code: {
          if (index !== 0) {
            text += this.translate.instant('pages.change-password.errors.point');
            if (this.translate.currentLang === 'en') {
              textValue += this.translate.instant('pages.change-password.errors.it');
            }
          }
          textValue += this.translate.instant('pages.change-password.errors.' + errorCode);
          if (index === 0) {
            checkForOtherInitialText = true;
          }
          else { textValue = this.capitalize(textValue); }
          break;
        }
        case ErrorType._4408.code: {
          textValue += this.translate.instant('pages.change-password.errors.' + errorCode);
          break;
        }
        default: break;
      }
      text +=  textValue;
    });
    text += this.translate.instant('pages.change-password.errors.point');
    if (checkForInitialText) {
      text = initialText + text;
    }
    else if (checkForOtherInitialText) {
      text = otherInitialText + text;
    }
    return text;
  }

  private capitalize = (s: string) => {
    if (typeof s !== 'string') { return ''; }
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  showPassword(el: ElementRef) {
    // console.log(el);
    this.inputType = 'text';
  }
  hidePassword(el: ElementRef) {
    this.inputType = 'password';
    this.showButton.nativeElement.blur();
  }
  checkType() {
    // return this.inputType;
    return this.hide ? 'password' : 'text';
  }

  toggleShowPassword(){
    console.log('toggleShowPassword');
    this.hide = !this.hide;
  }

  checkTypeConfirm() {
    return this.hideConfirm ? 'password' : 'text';
  }

  toggleShowPasswordConfirm(){
    this.hideConfirm = !this.hideConfirm;
  }

  ariaLabelShowPasswordConfirm() {
    if (this.hideConfirm) {
      return this.translate.instant('buttons.hidePasswordConfirm');
    } else {
      return this.translate.instant('buttons.showPasswordConfirm');
    }
  }

  ariaLabelShowPassword() {
    if (this.hide) {
      return this.translate.instant('buttons.hidePassword');
    } else {
      return this.translate.instant('buttons.showPassword');
    }
  }

  formValid() {
    let formValid = false;
    if (this.passwordForm.valid
      && this.getForm.newPassword.value !== ''
      && this.getForm.confirmPassword.value !== ''
    ) { formValid = true; }
    return formValid;
  }

}
